import {PlusIcon, TrashIcon} from '@heroicons/react/solid';
import React from 'react';

import {Button, Input, Modal, TextArea} from '@/components';
import type {BusinessRFI} from '@/types';
import {logRejection} from '@/utils/logRejection';
import {formatProvider} from '@/utils/merchant';

import {useBusinessRFISend} from './useBusinessRFISend';

type SendBusinessRFIModalProps = {
  businessId: string;
  isOpen: boolean;
  dataCy?: string;
  onClose: () => void;
  /**
   * Pass the existing DRAFT to name its documents instead of opening a new
   * request. A processor already created it and set businessId/description; an
   * admin is only filling in the one thing the rail could never tell us.
   */
  rfi?: BusinessRFI;
};

const SendBusinessRFIModal = ({
  businessId,
  isOpen,
  onClose,
  dataCy,
  rfi,
}: SendBusinessRFIModalProps) => {
  const {state, dispatch, isLoading, handleSubmit, isPromotingDraft} =
    useBusinessRFISend(businessId, onClose, rfi);

  return (
    <Modal
      isOpen={isOpen}
      dataCy={dataCy ? `${dataCy}-create-rfi` : undefined}
      onClose={onClose}
      title={isPromotingDraft ? 'Request Documents' : 'Create RFI'}
      width="w-10/12 sm:w-10/12 md:w-7/12 lg:w-4/12"
      closeOnOutsideClick
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          // `handleSubmit` toasts its own failures, so this only fires if that
          // handler itself throws. Log rather than swallow silently.
          handleSubmit().catch(logRejection('Failed to submit business RFI'));
        }}
        className="flex flex-col gap-4"
      >
        {/* dlocal's own words are the only clue about what it wants, so show them
            verbatim next to the field where the admin decides. */}
        {isPromotingDraft && rfi?.processorMessage && (
          <div className="rounded-lg bg-muted p-3 text-sm">
            <span className="font-medium">
              {rfi.processor ? formatProvider(rfi.processor) : 'Processor'}{' '}
              reported:
            </span>{' '}
            {rfi.processorMessage}
          </div>
        )}
        {isPromotingDraft && (
          <p className="text-sm text-gray-600 dark:text-gray-300">
            The business is not notified until you request at least one
            document.
          </p>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-100 mb-2">
            Requested Documents
          </label>
          {state.requestedDocuments.map((doc, index) => (
            <div key={index} className="flex items-center gap-2 mb-2">
              <Input
                data-cy={
                  dataCy ? `${dataCy}-request-document-input` : undefined
                }
                value={doc}
                placeholder="e.g. Invoice"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  dispatch({type: 'SET_DOC', index, value: e.target.value})
                }
                className="w-full isFull"
              />
              <Button
                variant="ghost"
                colorScheme="red"
                type="button"
                onClick={() => dispatch({type: 'REMOVE_DOC', index})}
                disabled={state.requestedDocuments.length === 1}
                dataCy={
                  dataCy ? `${dataCy}-request-document-delete` : undefined
                }
              >
                <TrashIcon className="w-4 h-4" />
              </Button>
            </div>
          ))}
          <div className="flex justify-end">
            <Button
              dataCy={
                dataCy ? `${dataCy}-request-document-add-field` : undefined
              }
              variant="outline"
              colorScheme="cyan"
              type="button"
              onClick={() => dispatch({type: 'ADD_DOC'})}
              leftIcon={<PlusIcon className="w-4 h-4" />}
            >
              Add Field
            </Button>
          </div>
        </div>

        {/* Neither applies to a draft: the processor already set description,
            and there is no customer email on a processor-raised request. */}
        {!isPromotingDraft && (
          <Input
            data-cy={
              dataCy ? `${dataCy}-request-document-email-input` : undefined
            }
            label="Customer Email (optional)"
            type="email"
            value={state.customerEmail || ''}
            placeholder="customer@email.com"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              dispatch({
                type: 'SET_FIELD',
                field: 'customerEmail',
                value: e.target.value,
              })
            }
            className="w-full isFull"
          />
        )}

        {/* Create only. Naming the payment is how an admin asks again after a
            rail-raised request was closed but the rail still wants more:
            nothing re-opens that automatically. */}
        {!isPromotingDraft && (
          <Input
            className="w-full isFull"
            label="Transaction ID (optional)"
            placeholder="Held payment this request is about"
            value={state.transactionId ?? ''}
            data-cy={
              dataCy
                ? `${dataCy}-request-document-transaction-id-input`
                : undefined
            }
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              dispatch({
                type: 'SET_FIELD',
                field: 'transactionId',
                value: e.target.value,
              })
            }
          />
        )}

        <TextArea
          data-cy={
            dataCy ? `${dataCy}-request-document-text-area-input` : undefined
          }
          label={
            isPromotingDraft
              ? 'Note for the business (optional)'
              : 'Description'
          }
          value={(isPromotingDraft ? state.note : state.description) || ''}
          placeholder={
            isPromotingDraft
              ? 'Add context for what is needed and why...'
              : 'Add context for the business...'
          }
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
            dispatch({
              type: 'SET_FIELD',
              field: isPromotingDraft ? 'note' : 'description',
              value: e.target.value,
            })
          }
          aria-rowspan={4}
        />

        <div className="flex justify-end gap-3 mt-2">
          <Button
            variant="ghost"
            colorScheme="cyan"
            type="button"
            onClick={onClose}
            dataCy={dataCy ? `${dataCy}-request-document-cancel` : undefined}
          >
            Cancel
          </Button>
          <Button
            colorScheme="cyan"
            type="submit"
            isLoading={isLoading}
            loadingText={isPromotingDraft ? 'Requesting...' : 'Creating...'}
            dataCy={dataCy ? `${dataCy}-request-document-confirm` : undefined}
          >
            {isPromotingDraft ? 'Request Documents' : 'Create RFI'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default SendBusinessRFIModal;
